import { consoleIfNotProd } from 'chatspot-core/loggers/consoleIfNotProd';
import { CHAT_AGENT, SALES_COPILOT } from 'chatspot-core/types/CopilotAssistantTypes';
import { useSendOnCopilotChannel } from 'copilot-toolkit/channel/hooks/useSendOnCopilotChannel';
import Raven from 'raven-js';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAssistant, selectAssistantType } from '../assistants/selectors/assistants';
import { updateAssistant } from '../assistants/slices/assistants';
import { useIsUngatedForCopilotV2 } from '../gating/hooks/useIsUngatedForCopilotV2';
import { setCopilotThreadIdInSuperstore } from '../superstore/utils/copilotThreadId';
import { copilotV2GetThreadWithMessages } from '../threads/actions/copilotV2GetThreadWithMessages';
import { resumeSalesCopilotThread } from '../threads/actions/resumeSalesCopilotThread';
import { resumeThread } from '../threads/actions/resumeThread';
import { useCreateNewThread } from '../threads/hooks/useCreateNewThread';
import { selectThreads } from '../threads/selectors/threadsSelectors';
import { getLatestBreezeCopilotThreadId, getLatestChatAgentOrBreezeCopilotThreadId, getLatestSalesCopilotThreadId } from '../threads/utils/getLatestBreezeCopilotThreadId';
import { usageTracker } from '../utils/usageTracker';
export const useAssistantSwitcher = () => {
  const dispatch = useDispatch();
  const createNewThread = useCreateNewThread();
  const assistantType = useSelector(selectAssistantType);
  const currentAssistant = useSelector(selectAssistant);
  const isUngatedForCopilotV2 = useIsUngatedForCopilotV2();
  const threads = useSelector(selectThreads);
  const sendFocusInput = useSendOnCopilotChannel('FOCUS_INPUT');
  const handleAssistantUpdate = useCallback((newAssistantType, assistant) => {
    dispatch(updateAssistant({
      type: newAssistantType,
      name: assistant === null || assistant === void 0 ? void 0 : assistant.name,
      id: assistant === null || assistant === void 0 ? void 0 : assistant.id,
      welcomeMessage: assistant === null || assistant === void 0 ? void 0 : assistant.welcomeMessage
    }));
    if (newAssistantType === CHAT_AGENT && (currentAssistant === null || currentAssistant === void 0 ? void 0 : currentAssistant.id) === (assistant === null || assistant === void 0 ? void 0 : assistant.id)) {
      return;
    }
    if (newAssistantType !== CHAT_AGENT && newAssistantType === assistantType) {
      return;
    }
    usageTracker.track('interaction', {
      action: 'assistant_switcher_clicked',
      type: newAssistantType
    });
    if (newAssistantType === SALES_COPILOT) {
      const latestSalesCopilotThreadId = getLatestSalesCopilotThreadId(threads);
      if (latestSalesCopilotThreadId) {
        dispatch(resumeSalesCopilotThread({
          threadId: String(latestSalesCopilotThreadId)
        })).catch(e => {
          Raven.captureException(e, {
            extra: {
              component: 'AssistantSwitcher',
              action: 'handleAssistantClick',
              messagesVersion: 'salesCopilot'
            }
          });
        });
      } else {
        createNewThread({
          usageTrackerScreen: 'CHAT',
          usageTrackerSource: 'new_thread_button',
          assistantType: newAssistantType
        });
      }
    } else if (isUngatedForCopilotV2) {
      const latestCopilotV2ThreadId = getLatestChatAgentOrBreezeCopilotThreadId(threads, assistant === null || assistant === void 0 ? void 0 : assistant.id);
      if (latestCopilotV2ThreadId) {
        dispatch(copilotV2GetThreadWithMessages({
          threadId: latestCopilotV2ThreadId
        })).unwrap().then(() => {
          void setCopilotThreadIdInSuperstore(latestCopilotV2ThreadId);
        }).catch(() => {
          Raven.captureException(new Error('Error getting thread'), {
            extra: {
              component: 'AssistantSwitcher',
              action: 'handleAssistantClick',
              messagesVersion: 'v1'
            }
          });
          createNewThread({
            usageTrackerScreen: 'CHAT',
            usageTrackerSource: 'new_thread_button',
            assistantType: newAssistantType,
            customCopilotId: assistant === null || assistant === void 0 ? void 0 : assistant.id
          });
        });
      } else {
        createNewThread({
          usageTrackerScreen: 'CHAT',
          usageTrackerSource: 'new_thread_button',
          assistantType: newAssistantType,
          customCopilotId: assistant === null || assistant === void 0 ? void 0 : assistant.id
        });
      }
      /** Is gated for Copilot V2 or Interactive Mode */
    } else {
      const latestBreezeCopilotThreadId = getLatestBreezeCopilotThreadId(threads, newAssistantType);
      if (latestBreezeCopilotThreadId) {
        dispatch(resumeThread({
          id: latestBreezeCopilotThreadId,
          offset: undefined,
          limit: 10
        })).catch(e => Raven.captureException(e, {
          extra: {
            component: 'AssistantSwitcher',
            action: 'handleAssistantClick',
            messagesVersion: 'v1'
          }
        }));
        try {
          void setCopilotThreadIdInSuperstore(latestBreezeCopilotThreadId);
        } catch (e) {
          consoleIfNotProd.error('Error setting copilot thread id in superstore', e);
        }
      } else {
        createNewThread({
          usageTrackerScreen: 'CHAT',
          usageTrackerSource: 'new_thread_button',
          assistantType: newAssistantType,
          customCopilotId: assistant === null || assistant === void 0 ? void 0 : assistant.id
        });
      }
    }
    sendFocusInput();
  }, [assistantType, currentAssistant === null || currentAssistant === void 0 ? void 0 : currentAssistant.id, createNewThread, dispatch, sendFocusInput, threads, isUngatedForCopilotV2]);
  return {
    updateAssistantType: handleAssistantUpdate
  };
};